"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextInputHelper = void 0;
/**
 * TextInputHelper
 * This textfield wrapper provides parsing, validation, and formatting functionality to textfields. The
 * formatting is removed from the field when it has focus and reapplied on blur.
 *
 * This textfield wrapper does not format while the user is typing as there are accessibility issues
 * with screenreaders. They read out the last character added rather than what was typed, i.e if the field
 * contains "123" and "4" is pressed, the new value of "1234" is formatted to "1,234" and the screenreader reads out
 * the "," instead of the "4".
 *
 * The other complication with formatting while typing is the need to retain the cursor position. If the user is
 * deleting text from the start of the field and it's formatted, the cursor is moved to the end of the field. The
 * position can be maintained however we need to know which key was pressed and this is only known on keyup or keydown
 * events and not the standard onchange event.
 */
var PropTypes = require("prop-types");
var React = require("react");
var TextField_1 = require("../TextField");
/**
 * TextInputHelper is used for adding validation and formatting to a text field
 */
var TextInputHelper = /** @class */ (function (_super) {
    __extends(TextInputHelper, _super);
    function TextInputHelper(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Set focus to the TextField
         */
        _this.focus = function () {
            if (_this.refTextField.current) {
                _this.refTextField.current.focus();
            }
        };
        /**
         * When the field gains focus the formatting is removed
         */
        _this.onFocus = function (e) {
            var value = _this.state.value;
            // Remove formatting
            if (typeof _this.props.parser === 'function') {
                value = _this.props.parser(value);
            }
            // Update the state
            _this.setState({
                focusCount: _this.state.focusCount + 1,
                value: value,
            });
            if (typeof _this.props.onFocus === 'function') {
                _this.props.onFocus(e);
            }
        };
        /**
         * When the field loses focuses the value is formatted and the state updated
         * The blur count is tracked as it's used to determine if error state should be displayed
         */
        _this.onBlur = function (e) {
            var value = _this.state.value;
            // Apply formatting to the field
            if (typeof _this.props.formatter === 'function') {
                value = _this.props.formatter(value);
            }
            // Run validation
            _this.validate(value, false);
            // Update the state
            var blurCount = _this.state.blurCount + 1;
            _this.setState({
                blurCount: blurCount,
                showErrors: blurCount > 0,
            });
            if (typeof _this.props.onBlur === 'function') {
                _this.props.onBlur(e);
            }
        };
        /**
         * When the value changes the field is validated and state updated
         */
        _this.onChange = function (name, value, event) {
            // Remove formatting
            if (typeof _this.props.parser === 'function') {
                value = _this.props.parser(value);
            }
            // Run validation
            var validationResult = _this.validate(value, false);
            // Update the state
            _this.setState(__assign(__assign({}, validationResult), { value: value }));
            // Notify onchange callback
            if (typeof _this.props.onChange !== 'undefined') {
                _this.props.onChange(name, value, event);
            }
        };
        _this.validate = function (value, externallyTriggered) {
            if (externallyTriggered === void 0) { externallyTriggered = true; }
            // Run validation
            var validationResult = _this.getValidationResults(value);
            var newState = __assign(__assign({}, validationResult), { value: value });
            if (externallyTriggered) {
                // When the validation is triggered by a blur event the blur event controls the visibility of error messages,
                // when the validation is triggered externally the errors are shown regardless of the blur state
                newState.showErrors = true;
            }
            // Update the state
            _this.setState(newState);
            return validationResult;
        };
        /**
         * Run all validation functions and return a result with error message
         */
        _this.getValidationResults = function (value) {
            var result = {
                errormessage: _this.state.errormessage,
                isValid: _this.state.isValid,
            };
            if (typeof _this.props.validators !== 'object' ||
                typeof _this.props.validators.length === 'undefined' ||
                _this.props.validators.length === 0) {
                return result;
            }
            // The field is valid until it fails a validation rule
            result.isValid = true;
            _this.props.validators.forEach(function (validator) {
                if (!result.isValid) {
                    // Don't run anymore validation rules after one fails
                    return;
                }
                // Pass into validation function
                result.isValid = validator.method(value || _this.state.value);
                // Set the error message when invalid
                if (!result.isValid) {
                    result.errormessage = validator.errormessage;
                }
            });
            return result;
        };
        var value = props.value;
        if (typeof props.formatter !== 'undefined') {
            value = props.formatter(value);
        }
        _this.state = {
            blurCount: 0,
            errormessage: '',
            focusCount: 0,
            isValid: true,
            showErrors: false,
            value: value,
        };
        // Create a ref if one is not provided
        _this.refTextField = props.forwardedRef || React.createRef();
        return _this;
    }
    TextInputHelper.prototype.render = function () {
        // Note a "let" is used as the "value" prop is changed depending on whether
        // we use the local state or a parent store for the value.
        /* tslint:disable prefer-const */
        var _a = this.props, formatter = _a.formatter, parser = _a.parser, validators = _a.validators, onChange = _a.onChange, value = _a.value, forwardedRef = _a.forwardedRef, textfieldprops = __rest(_a, ["formatter", "parser", "validators", "onChange", "value", "forwardedRef"]) // tslint:disable-line
        ;
        /* tslint:enable prefer-const */
        // "value" is copied from props into state
        value = this.state.value;
        // Validation is shown after the field loses focus
        var isValid = true;
        if (!this.state.isValid && this.state.showErrors) {
            isValid = this.state.isValid;
        }
        return (React.createElement(TextField_1.TextField, __assign({}, textfieldprops, { value: value, onFocus: this.onFocus, onChange: this.onChange, onBlur: this.onBlur, errormessage: this.state.errormessage, isValid: isValid, ref: this.refTextField })));
    };
    TextInputHelper.getDerivedStateFromProps = function (props, state) {
        if (typeof props.onChange !== 'undefined') {
            // If a TextInputHelper is given an onChange callback, it's expected the
            // value is being managed outside of the local state.
            var value = props.value;
            if (typeof props.formatter !== 'undefined' && state.focusCount === state.blurCount) {
                // Apply formatting if the field is not currently in focus
                value = props.formatter(value);
            }
            if (typeof props.parser !== 'undefined' && state.focusCount !== state.blurCount) {
                // Parse the field if the field is being edited
                value = props.parser(value);
            }
            return {
                value: value,
            };
        }
        return null;
    };
    return TextInputHelper;
}(React.Component));
exports.TextInputHelper = TextInputHelper;
/* tslint:disable:no-string-literal */
TextInputHelper['propTypes'] = {
    formatter: PropTypes.func,
    parser: PropTypes.func,
    validators: PropTypes.arrayOf(PropTypes.shape({
        errormessage: PropTypes.string,
        method: PropTypes.func,
    })),
};
/* tslint:enable:no-string-literal */
